import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { TitleLarge } from '../components/Typography';
import { colors, typography } from '../theme/tokens';
import Button from '../components/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, X, List } from '@phosphor-icons/react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { saveRecipeSteps } from '../api/recipes';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import { createMuxUpload } from '../utils/mux';
// @ts-ignore
import confetti from 'canvas-confetti';

const PageContainer = styled.div`
  padding: 2rem;
  width: 100%;
  max-width: 100%;
`;

const FormContainer = styled.div`
  padding: 2rem;
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
`;

const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const Step = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .step-number {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${props =>
      props.isCompleted
        ? colors.brand.laurel
        : props.isActive
        ? colors.neutral.white
        : colors.neutral.offWhite};
    border: 2px solid ${props =>
      props.isCompleted || props.isActive
        ? colors.brand.laurel
        : colors.neutral.lightCharcoal};
    color: ${props =>
      props.isCompleted
        ? colors.neutral.white
        : props.isActive
        ? colors.brand.laurel
        : colors.neutral.darkCharcoal};
    font-family: ${typography.label.large.fontFamily};
    font-size: ${typography.label.large.fontSize};
  }

  .step-label {
    color: ${props =>
      props.isActive
        ? colors.brand.laurel
        : colors.neutral.darkCharcoal};
    font-family: ${typography.label.medium.fontFamily};
    font-size: ${typography.label.medium.fontSize};
  }
`;

const StepConnector = styled.div<{ isCompleted: boolean }>`
  height: 2px;
  width: 4rem;
  background-color: ${props =>
    props.isCompleted
      ? colors.brand.laurel
      : colors.neutral.lightCharcoal};
`;

const Tick = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="9" r="9" fill={colors.brand.laurel} />
    <path d="M5 9.5L8 12.5L13 7.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 2rem;
`;

const StepCard = styled.div<{ isDragging: boolean }>`
  background: ${colors.neutral.offWhite};
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  margin-bottom: 1rem;
  transition: background-color 0.2s ease;
  background-color: ${props => props.isDragging ? colors.neutral.lightCharcoal : colors.neutral.offWhite};
`;

const StepMainContent = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  width: 100%;
`;

const UploadFieldsContainer = styled.div`
  display: flex;
  gap: 2rem;
  padding-top: 1rem;
  border-top: 1px solid ${colors.neutral.lightCharcoal};
`;

const StepFields = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  width: 100%;
`;

const StepNumberContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DragHandle = styled.div`
  cursor: grab;
  color: ${colors.neutral.darkCharcoal};
  opacity: 0.5;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 4px;

  &:hover {
    opacity: 1;
    background-color: ${colors.neutral.lightCharcoal};
  }
`;

const StepNumberInput = styled.input`
  width: 48px;
  text-align: center;
  background: ${colors.neutral.white};
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 4px;
  font-size: 18px;
  font-family: ${typography.label.large.fontFamily};
  color: ${colors.neutral.black};
  margin-bottom: 16px;
`;

const InstructionTextArea = styled.textarea`
  flex: 2;
  min-height: 60px;
  background: ${colors.neutral.white};
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 4px;
  font-size: 16px;
  font-family: ${typography.paragraph.medium.fontFamily};
  padding: 0.75rem;
  margin-bottom: 16px;
  color: ${colors.neutral.black};

  &::placeholder {
    color: ${colors.neutral.lightCharcoal};
  }
`;

const UploadField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RemoveStepButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: ${colors.reds.bright};
  cursor: pointer;
  font-size: 18px;
`;

const AddStepButton = styled(Button)`
  margin-top: 2rem;
  align-self: flex-start;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ThumbnailPreview = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  margin-top: 0.5rem;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1rem;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-family: ${typography.label.medium.fontFamily};
  font-size: ${typography.label.medium.fontSize};
  color: ${colors.neutral.darkCharcoal};
`;

const RadioInput = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
`;

const ExplanatoryLabel = styled.div`
  font-family: ${typography.label.small.fontFamily};
  font-size: ${typography.label.small.fontSize};
  color: ${colors.neutral.charcoal};
  margin-top: 0.5rem;
  line-height: 1.4;
`;

const UploadProgress = styled.div`
  font-family: ${typography.label.small.fontFamily};
  font-size: ${typography.label.small.fontSize};
  color: ${colors.brand.laurel};
  margin-top: 0.5rem;
`;

const UploadError = styled.div`
  font-family: ${typography.label.small.fontFamily};
  font-size: ${typography.label.small.fontSize};
  color: ${colors.reds.bright};
  margin-top: 0.5rem;
`;

interface StepData {
  id: string;
  step: number;
  instructions: string;
  thumbnail: string;
  video: File | null;
  mediaType: 'thumbnail' | 'video' | 'none';
  muxAssetId?: string;
  muxPlaybackId?: string;
  uploadStatus?: 'pending' | 'uploading' | 'completed' | 'error';
  uploadError?: string;
  useRecipeImage?: boolean;
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const RecipeInstructions: React.FC = () => {
  const navigate = useNavigate();
  const { recipeId } = useParams<{ recipeId: string }>();
  const { showToast } = useToast();
  const { user } = useAuth();
  const [steps, setSteps] = useState<StepData[]>([
    { id: Date.now().toString(), step: 1, instructions: '', thumbnail: '', video: null, mediaType: 'none', useRecipeImage: true }
  ]);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [recipeImageUrl, setRecipeImageUrl] = useState<string>('');

  useEffect(() => {
    const fetchRecipeData = async () => {
      if (!recipeId) return;
      
      try {
        // Fetch recipe image
        const { data: recipeData, error: recipeError } = await supabase
          .from('Recipes')
          .select('ImageURL')
          .eq('RecipeId', recipeId)
          .single();

        if (recipeError) throw recipeError;
        setRecipeImageUrl(recipeData.ImageURL || '');

        // Fetch steps
        const { data, error } = await supabase
          .from('RecipeSteps')
          .select('*')
          .eq('RecipeID', recipeId)
          .order('Step', { ascending: true });

        if (error) throw error;

        if (data && data.length > 0) {
          setSteps(data.map(step => ({
            id: step.id.toString(),
            step: step.Step,
            instructions: step.Instructions,
            thumbnail: step.Thumbnail || '',
            video: null,
            mediaType: step.Thumbnail ? 'thumbnail' : step.MuxAssetId ? 'video' : 'none',
            muxAssetId: step.MuxAssetId || undefined,
            muxPlaybackId: step.MuxPlaybackId || undefined,
            uploadStatus: step.upload_error ? 'error' : 'completed',
            uploadError: step.upload_error || undefined,
            useRecipeImage: !step.Thumbnail && !step.MuxAssetId
          })));
        }
      } catch (error) {
        console.error('Error fetching recipe data:', error);
        showToast('Failed to load recipe data', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecipeData();
  }, [recipeId, showToast]);

  const handleInstructionChange = (idx: number, value: string) => {
    setSteps(prev => prev.map((step, i) => i === idx ? { ...step, instructions: value } : step));
  };

  const handleAddStep = () => {
    setSteps(prev => [
      ...prev,
      { id: Date.now().toString(), step: prev.length + 1, instructions: '', thumbnail: '', video: null, mediaType: 'none', useRecipeImage: true }
    ]);
  };

  const handleRemoveStep = (idx: number) => {
    setSteps(prev => prev.filter((_, i) => i !== idx).map((step, i) => ({ ...step, step: i + 1 })));
  };

  const handleThumbnailChange = (idx: number, file: File | null) => {
    // TODO: Integrate Cloudinary upload logic here (copy from CreateRecipe)
    // For now, just show a local preview
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setSteps(prev => prev.map((step, i) => i === idx ? { ...step, thumbnail: reader.result as string } : step));
    };
    reader.readAsDataURL(file);
  };

  const handleVideoChange = (idx: number, file: File | null) => {
    setSteps(prev => prev.map((step, i) => i === idx ? { ...step, video: file } : step));
  };

  const handleVideoUpload = async (idx: number, file: File) => {
    if (!user?.id) {
      showToast('User not authenticated', 'error');
      return;
    }

    // Update step to show uploading status
    setSteps(prev => prev.map((step, i) => 
      i === idx ? { ...step, uploadStatus: 'uploading', uploadError: undefined } : step
    ));

    try {
      const muxResponse = await createMuxUpload(file, `Step ${idx + 1} video`, user.id);
      
      // Create thumbnail URL from playback ID
      const thumbnailUrl = `https://image.mux.com/${muxResponse.playback_id}/thumbnail.png`;
      
      // Update step with Mux data and thumbnail
      setSteps(prev => prev.map((step, i) => 
        i === idx ? { 
          ...step, 
          muxAssetId: muxResponse.id,
          muxPlaybackId: muxResponse.playback_id,
          thumbnail: thumbnailUrl, // Use Mux thumbnail as preview
          uploadStatus: 'completed',
          uploadError: undefined
        } : step
      ));

      showToast('Video uploaded successfully', 'success');
    } catch (error) {
      console.error('Video upload error:', error);
      setSteps(prev => prev.map((step, i) => 
        i === idx ? { 
          ...step, 
          uploadStatus: 'error',
          uploadError: error instanceof Error ? error.message : 'Upload failed'
        } : step
      ));
      showToast('Failed to upload video', 'error');
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(steps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update step numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      step: index + 1
    }));

    setSteps(updatedItems);
  };

  const handleMediaTypeToggle = (idx: number, newType: 'thumbnail' | 'video') => {
    setSteps(prev => prev.map((step, i) => {
      if (i === idx) {
        return {
          ...step,
          mediaType: newType,
          thumbnail: newType === 'thumbnail' ? step.thumbnail : '',
          video: newType === 'video' ? step.video : null,
          useRecipeImage: false // When user selects a media type, don't use recipe image
        };
      }
      return step;
    }));
  };

  const handleBack = () => {
    if (recipeId) {
      navigate(`/recipes/create/${recipeId}`);
    } else {
      navigate('/recipes/create');
    }
  };

  const handleNext = async () => {
    if (!recipeId) {
      showToast('Recipe ID not found', 'error');
      return;
    }
    try {
      await saveRecipeSteps(recipeId, steps, recipeImageUrl);
      showToast('Recipe steps saved successfully', 'success');
      navigate('/recipes');
    } catch (error) {
      console.error('Failed to save recipe steps:', error);
      showToast('Failed to save recipe steps', 'error');
    }
  };

  const handlePublish = async () => {
    if (!recipeId || !user?.id) {
      showToast('Recipe ID not found or user not authenticated', 'error');
      return;
    }
    try {
      // Get the creator's ID from CreatorDetails using the auth user's ID
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();

      if (creatorError) {
        throw new Error('Failed to verify creator account');
      }

      if (!creatorDetails) {
        throw new Error('No creator details found for user');
      }

      const creatorId = creatorDetails.id;

      await saveRecipeSteps(recipeId, steps, recipeImageUrl);
      
      // Update the recipe's Publish field to true and verify it belongs to the current creator
      const { error } = await supabase
        .from('Recipes')
        .update({ Publish: true })
        .eq('RecipeId', recipeId)
        .eq('UserID', creatorId);
      
      if (error) throw error;
      
      showToast('Recipe published successfully', 'success');
      navigate('/recipes');
      confetti({
        particleCount: 200,
        spread: 180,
        origin: { y: 0.6 },
        colors: ['#177508', '#F6B83F', '#CFE531', '#E358C2', '#A0EA95', '#0E4805']
      });
    } catch (error) {
      console.error('Failed to publish recipe:', error);
      showToast('Failed to publish recipe', 'error');
    }
  };

  return (
    <PageContainer>
      <FormContainer>
        <TitleLarge>Step 2: Instructions</TitleLarge>
        <StepperContainer>
          <Step isActive={false} isCompleted={true}>
            <span className="step-number"><Tick /></span>
            <span className="step-label">Recipe Details</span>
          </Step>
          <StepConnector isCompleted={true} />
          <Step isActive={true} isCompleted={false}>
            <span className="step-number">2</span>
            <span className="step-label">Instructions</span>
          </Step>
        </StepperContainer>
        {isLoading ? (
          <div>Loading steps...</div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="steps">
              {(provided) => (
                <StepsList
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {steps.map((step, idx) => (
                    <Draggable
                      key={step.id}
                      draggableId={step.id}
                      index={idx}
                    >
                      {(provided, snapshot) => (
                        <StepCard
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          isDragging={snapshot.isDragging}
                        >
                          <StepMainContent>
                            <StepNumberContainer>
                              <DragHandle {...provided.dragHandleProps}>
                                <List size={20} weight="bold" />
                              </DragHandle>
                              <StepNumberInput type="number" value={step.step} disabled />
                            </StepNumberContainer>
                            <InstructionTextArea
                              placeholder={`Step ${step.step} instructions...`}
                              value={step.instructions}
                              onChange={e => handleInstructionChange(idx, e.target.value)}
                            />
                          </StepMainContent>
                          <UploadFieldsContainer>
                            <ToggleContainer>
                              <RadioOption>
                                <RadioInput
                                  type="radio"
                                  name={`media-type-${step.id}`}
                                  value="thumbnail"
                                  checked={step.mediaType === 'thumbnail'}
                                  onChange={() => handleMediaTypeToggle(idx, 'thumbnail')}
                                />
                                Thumbnail
                              </RadioOption>
                              <RadioOption>
                                <RadioInput
                                  type="radio"
                                  name={`media-type-${step.id}`}
                                  value="video"
                                  checked={step.mediaType === 'video'}
                                  onChange={() => handleMediaTypeToggle(idx, 'video')}
                                />
                                Video
                              </RadioOption>
                            </ToggleContainer>
                            <ExplanatoryLabel>
                              If you don't add custom image or video, the main recipe image will be used for this step
                            </ExplanatoryLabel>
                            {step.mediaType === 'thumbnail' ? (
                              <UploadField>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => fileInputRefs.current[idx]?.click()}
                                >
                                  <Upload size={20} />
                                  Upload Thumbnail
                                </Button>
                                <HiddenFileInput
                                  type="file"
                                  accept="image/*"
                                  ref={el => fileInputRefs.current[idx] = el}
                                  onChange={e => handleThumbnailChange(idx, e.target.files?.[0] || null)}
                                />
                                {step.thumbnail && (
                                  <ThumbnailPreview src={step.thumbnail} alt={`Step ${step.step} thumbnail`} />
                                )}
                              </UploadField>
                            ) : (
                              <UploadField>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => fileInputRefs.current[idx]?.click()}
                                >
                                  <Upload size={20} />
                                  Upload Video
                                </Button>
                                <HiddenFileInput
                                  type="file"
                                  accept="video/*"
                                  ref={el => fileInputRefs.current[idx] = el}
                                  onChange={e => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleVideoChange(idx, file);
                                      handleVideoUpload(idx, file);
                                    }
                                  }}
                                />
                                {step.video && step.uploadStatus !== 'completed' && (
                                  <ThumbnailPreview src={URL.createObjectURL(step.video)} alt={`Step ${step.step} video preview`} />
                                )}
                                {step.thumbnail && step.mediaType === 'video' && step.uploadStatus === 'completed' && (
                                  <ThumbnailPreview src={step.thumbnail} alt={`Step ${step.step} video thumbnail`} />
                                )}
                                {step.uploadStatus === 'uploading' && (
                                  <UploadProgress>Uploading video...</UploadProgress>
                                )}
                                {step.uploadStatus === 'completed' && (
                                  <UploadProgress>Video uploaded successfully!</UploadProgress>
                                )}
                                {step.uploadError && (
                                  <UploadError>{step.uploadError}</UploadError>
                                )}
                              </UploadField>
                            )}
                          </UploadFieldsContainer>
                          <RemoveStepButton onClick={() => handleRemoveStep(idx)}>
                            <X size={24} />
                          </RemoveStepButton>
                        </StepCard>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </StepsList>
              )}
            </Droppable>
          </DragDropContext>
        )}
        <AddStepButton onClick={handleAddStep}>Add Step</AddStepButton>
        <ButtonContainer>
          <Button variant="outline" onClick={handleBack}>
            Back
          </Button>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button variant="outline" onClick={handleNext}>
              Save Draft
            </Button>
            <Button onClick={handlePublish}>
              Publish
            </Button>
          </div>
        </ButtonContainer>
      </FormContainer>
    </PageContainer>
  );
};

export default RecipeInstructions; 